/**
 * React Query hooks for dashboards.
 * React Query hooks for the corresponding entity.
 */

import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";

export type AccessibleFarmerProgramme = {
  userGuid: string;
  userName: string;
  userEmail: string;
  joinedDate: string | null;
  consentSigned: boolean;
  programmes: { guid: string; name: string; description: string }[];
};

class FarmerDashboardAPI {
  baseUrl: string;

  constructor() {
    this.baseUrl = `${import.meta.env.VITE_API_ENDPOINT}/v1/`;
  }

  async getAccessibleFarmerProgrammes(): Promise<AccessibleFarmerProgramme[]> {
    const res = await fetch(
      `${this.baseUrl}dashboardAccess/accessibleFarmers`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (!res.ok) return [];
    return (await res.json()) as AccessibleFarmerProgramme[];
  }
}

const dashboardApi = new FarmerDashboardAPI();

/**
 * Fetch accessible farmers with their programmes (gated by CorporateFarmerAccess).
 * Returns an empty array for corporate users without farmer-detail access.
 */
export function useAccessibleFarmerProgrammes(enabled = true) {
  return useQuery({
    queryKey: queryKeys.dashboards.accessibleFarmerProgrammes,
    queryFn: async () => {
      return dashboardApi.getAccessibleFarmerProgrammes();
    },
    enabled,
  });
}

/**
 * Fetch accessible farmer programmes directly (not a hook, for use in services).
 */
export async function fetchAccessibleFarmerProgrammesQuery(): Promise<
  AccessibleFarmerProgramme[]
> {
  return dashboardApi.getAccessibleFarmerProgrammes();
}
